/**
 * ContactMedia — card de contato compartilhado (tokens neutros do DS).
 */
import React, { useMemo, useState } from 'react';
import { AppIcon } from '../../../../../components/icons/AppIcon';
import { useAppContext } from '../../../../../contexts/AppContext';
import { useChatContext } from '../../../../../contexts/useChatContext';
import { safeParse, extractPhoneFromVCard } from './types.js';
import { MediaFallback } from './MediaFallback.js';

interface ContactMediaProps {
    content?: string;
}

export const ContactMedia: React.FC<ContactMediaProps> = ({ content }) => {
    const { setCurrentPage } = useAppContext();
    const chatContext = useChatContext();
    const [showDetails, setShowDetails] = useState(false);

    const data = useMemo(() => {
        const parsed = safeParse(content);
        return Array.isArray(parsed) ? parsed[0] : parsed;
    }, [content]);

    const phone = useMemo(() => {
        if (!data?.vcard) return null;
        return extractPhoneFromVCard(data.vcard);
    }, [data]);

    if (!data) return <MediaFallback type="contact" fallbackLabel="Contato inválido" />;

    const handleStartChat = () => {
        if (!phone) return;
        const cleanPhone = phone.replace(/\D/g, '');
        if (chatContext?.startNewConversation) {
            chatContext.startNewConversation(cleanPhone, data.displayName || cleanPhone);
            setCurrentPage('chat');
            return;
        }
        localStorage.setItem('CHAT_OPEN_CONVERSATION_PHONE', cleanPhone);
        setCurrentPage('chat');
    };

    return (
        <article className="chat-rich-card m-1.5 flex w-[min(100%,17rem)] flex-col overflow-hidden rounded-xl bg-card shadow-xs sm:w-[17.5rem]">
            <header className="flex items-center gap-3 px-3 py-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-secondary">
                    <AppIcon name="user" className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-default">
                        {data.displayName || 'Desconhecido'}
                    </div>
                    <div className="text-xs text-muted">Contato</div>
                </div>
                <button
                    type="button"
                    onClick={() => setShowDetails((v) => !v)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-subtle hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border"
                    title="Visualizar contato"
                    aria-expanded={showDetails}
                >
                    <AppIcon
                        name="arrow-right"
                        className={`h-4 w-4 transition-transform duration-200 ${showDetails ? 'rotate-90' : ''}`}
                    />
                </button>
            </header>

            {showDetails && phone && (
                <div className="border-t border-subtle bg-surface-subtle px-3 py-2.5">
                    <div className="text-[10px] font-medium uppercase tracking-wide text-muted">Telefone</div>
                    <div className="font-mono text-sm text-default">{phone}</div>
                </div>
            )}

            <button
                type="button"
                onClick={handleStartChat}
                className="flex min-h-11 w-full items-center justify-center gap-2 border-t border-subtle px-3 py-2.5 text-xs font-semibold text-secondary transition-colors hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border"
            >
                <AppIcon name="chat" className="h-4 w-4 text-secondary" />
                <span className="text-secondary">Conversar</span>
            </button>
        </article>
    );
};
